import { publicConfig } from "@/lib/public-config";

const MATCH_PUSH_SERVICE_WORKER_PATH = "/match-sw.js";

function canUseWindowApis() {
  return typeof window !== "undefined" && typeof navigator !== "undefined";
}

export function supportsPushNotifications() {
  return (
    canUseWindowApis() &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermissionState(): NotificationPermission | "unsupported" {
  if (!supportsPushNotifications()) return "unsupported";
  return Notification.permission;
}

export async function registerPushServiceWorker() {
  if (!supportsPushNotifications()) return null;

  return navigator.serviceWorker.register(MATCH_PUSH_SERVICE_WORKER_PATH, {
    scope: "/",
  });
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const normalized = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }

  return output;
}

export async function requestPushPermission() {
  if (!supportsPushNotifications()) return "unsupported" as const;
  if (Notification.permission === "granted") return "granted" as const;
  return Notification.requestPermission();
}

export async function getCurrentPushSubscription() {
  if (!supportsPushNotifications()) return null;
  const registration = await registerPushServiceWorker();
  return registration?.pushManager.getSubscription() ?? null;
}

export async function subscribeToMatchPushNotifications() {
  if (!supportsPushNotifications()) {
    throw new Error("push_unsupported");
  }

  const vapidPublicKey = publicConfig.pushVapidPublicKey;
  if (!vapidPublicKey) {
    throw new Error("push_vapid_missing");
  }

  const permission = await requestPushPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied" ? "push_permission_denied" : "push_permission_unavailable");
  }

  const registration = await registerPushServiceWorker();
  if (!registration) {
    throw new Error("push_registration_failed");
  }

  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
}

export async function unsubscribeFromMatchPushNotifications() {
  const existing = await getCurrentPushSubscription();
  if (!existing) return false;
  return existing.unsubscribe();
}

function getSubscriptionKey(subscription: PushSubscription, name: "p256dh" | "auth") {
  const key = subscription.getKey(name);
  if (!key) return "";

  const bytes = new Uint8Array(key);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export function serializePushSubscription(subscription: PushSubscription) {
  return {
    endpoint: subscription.endpoint,
    p256dh_key: getSubscriptionKey(subscription, "p256dh"),
    auth_key: getSubscriptionKey(subscription, "auth"),
  };
}

export function detectPushBrowserName(userAgent = canUseWindowApis() ? navigator.userAgent : "") {
  const source = userAgent.toLowerCase();

  if (source.includes("edg/")) return "Edge";
  if (source.includes("opr/") || source.includes("opera")) return "Opera";
  if (source.includes("chrome/")) return "Chrome";
  if (source.includes("firefox/")) return "Firefox";
  if (source.includes("safari/")) return "Safari";
  return "Navegador";
}

export function buildPushDeviceLabel() {
  if (!canUseWindowApis()) return "Web";

  const browserName = detectPushBrowserName();
  const navigatorWithUAData = navigator as Navigator & {
    userAgentData?: {
      platform?: string;
    };
  };
  const platform =
    navigatorWithUAData.userAgentData?.platform ||
    navigator.platform ||
    "Web";

  return `${browserName} · ${platform}`;
}
